import React from 'react';
import moment, { MomentInput } from 'moment';
import { useLocation } from 'react-router';
import { Filter, Button } from '@odin-labs/components';
import {
  ChangeType,
  useGetAvailableJobsiteWorkersQuery,
} from 'apollo/generated/client-operations';
import type {
  EditableFormSubmissionWorker,
  AvailableJobsiteWorker,
  FormSubmissionWorkerAssociationType,
} from 'containers/jobsiteFormSubmission/types';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { CheckIcon } from 'components/icons';
import { FormSubmissionSelectableWorker } from './FormSubmissionSelectableWorker';
import {
  WorkerFiltersOptions,
  WorkersFilters,
  getFilterItems,
  getFiltersOptions,
} from './FormSubmissionSelectableWorkers.tables';

export type FormSubmissionSelectableWorkersProps = {
  jobsiteId?: string;
  contractorIds?: string[];
  date?: MomentInput;
  associationType?: FormSubmissionWorkerAssociationType;
  allowMultipleContractors?: boolean;
  value?: EditableFormSubmissionWorker[];
  onChange?: (value: EditableFormSubmissionWorker[]) => void;
};

export const FormSubmissionSelectableWorkers = React.forwardRef<
  HTMLDivElement,
  FormSubmissionSelectableWorkersProps
>((props, ref): React.ReactElement => {
  const {
    value,
    onChange,
    jobsiteId,
    contractorIds,
    date,
    associationType,
    allowMultipleContractors,
  } = props;
  const location = useLocation();

  const {
    search,
    currentlyOnsite,
    trades,
    jobTitles,
    // orderBy: defaultSortField,
    // orderByDesc: isDescending,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams();

  const { data, loading } = useGetAvailableJobsiteWorkersQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteId || isUrlLoading,
    variables: {
      jobsiteId,
      jobsiteJobsiteWorkersInput: {
        contractorIds,
        search,
        onSite: currentlyOnsite
          ? currentlyOnsite.toLowerCase() === 'true'
          : null,
        onSiteDate: date ? moment.utc(date).toDate() : null,
        trades,
        jobTitles,
      },
    },
  });
  const jobsiteWorkers = data?.getJobsite.jobsiteWorkers.edges.map(
    ({ node }) => node,
  );

  const filtersOptions = React.useMemo<WorkerFiltersOptions>(
    () => ({
      trades: getFiltersOptions(jobsiteWorkers?.map((jw) => jw.trade)),
      jobTitles: getFiltersOptions(jobsiteWorkers?.map((jw) => jw.jobTitle)),
    }),
    [jobsiteWorkers],
  );

  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        filtersOptions,
        search,
        currentlyOnsite,
        trades,
        jobTitles,
      }),
    [filtersOptions, search, currentlyOnsite, trades, jobTitles],
  );

  const onFilterChangeHandler = (
    changedFilters: Partial<WorkersFilters>,
  ): void => {
    updateUrl({ page: null, ...changedFilters }, true, location?.hash);
  };

  const toggleItem = React.useCallback(
    (jobsiteWorker: AvailableJobsiteWorker): void => {
      const { jobsiteWorkerId } = jobsiteWorker;
      const submissionWorker = value?.find(
        (sw) => sw.jobsiteWorker.jobsiteWorkerId === jobsiteWorkerId,
      );
      let newValue: EditableFormSubmissionWorker[];
      if (submissionWorker) {
        // if worker has been previously removed then it will be updated
        if (submissionWorker?.changeType === ChangeType.Removed) {
          // eslint-disable-next-line unused-imports/no-unused-vars
          newValue = value.map((sw) => {
            // the worker gets restored if previously removed
            if (sw.id === submissionWorker.id) {
              const { changeType, ...newSw } = sw; // eslint-disable-line unused-imports/no-unused-vars
              return newSw;
            }
            return sw;
          });
        } else {
          newValue =
            submissionWorker.changeType === ChangeType.Created
              ? value.filter((sw) => sw !== submissionWorker)
              : value.map((sw) =>
                  sw.id !== submissionWorker.id
                    ? sw
                    : { ...sw, changeType: ChangeType.Removed },
                );
        }
      } else {
        // add new worker
        newValue = [
          ...(value ?? []),
          {
            jobsiteWorker,
            id: undefined,
            changeType: ChangeType.Created,
            associationType,
          },
        ];
      }

      if (newValue) {
        onChange?.(newValue);
      }
    },
    [onChange, value],
  );

  const selectedJobsiteWorkerIds = value
    ?.filter((sw) => sw.changeType !== ChangeType.Removed)
    .map((sw) => sw.jobsiteWorker.jobsiteWorkerId);
  const areAllSelected =
    jobsiteWorkers?.length &&
    jobsiteWorkers.every((jw) =>
      selectedJobsiteWorkerIds?.includes(jw.jobsiteWorkerId),
    );

  const selectAllClickHandler = React.useCallback(() => {
    if (
      jobsiteWorkers?.some(
        (jw) => !selectedJobsiteWorkerIds?.includes(jw.jobsiteWorkerId),
      )
    ) {
      const newValue = jobsiteWorkers.reduce<EditableFormSubmissionWorker[]>(
        (result, jobsiteWorker) => {
          const { jobsiteWorkerId } = jobsiteWorker;
          const submissionWorker = value?.find(
            (sw) => sw.jobsiteWorker.jobsiteWorkerId === jobsiteWorkerId,
          );
          if (submissionWorker) {
            // the worker gets restored if previously removed
            if (submissionWorker.changeType === ChangeType.Removed) {
              // eslint-disable-next-line unused-imports/no-unused-vars
              const { changeType, ...newSubmissionWorker } = submissionWorker;
              result.push(newSubmissionWorker);
            } else {
              result.push(submissionWorker);
            }
          } else {
            // add new worker
            result.push({
              jobsiteWorker,
              id: undefined,
              changeType: ChangeType.Created,
              associationType,
            });
          }
          return result;
        },
        [],
      );
      onChange?.(newValue);
    } else if (areAllSelected) {
      const newValue = jobsiteWorkers.reduce<EditableFormSubmissionWorker[]>(
        (result, jobsiteWorker) => {
          const { jobsiteWorkerId } = jobsiteWorker;
          const submissionWorker = value?.find(
            (sw) => sw.jobsiteWorker.jobsiteWorkerId === jobsiteWorkerId,
          );
          if (submissionWorker) {
            if (!submissionWorker.changeType) {
              result.push({
                ...submissionWorker,
                changeType: ChangeType.Removed,
              });
            } else if (submissionWorker.changeType !== ChangeType.Created) {
              result.push(submissionWorker);
            }
          }
          return result;
        },
        [],
      );
      onChange?.(newValue);
    }
  }, [
    onChange,
    value,
    jobsiteWorkers,
    selectedJobsiteWorkerIds,
    areAllSelected,
  ]);

  return (
    <div className="odin-flex odin-flex-col odin-gap-y-4.5">
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFilterChangeHandler}
        leftContainerStyle="sm:odin-pr-2.5"
      >
        <Button
          theme={areAllSelected ? 'secondary' : 'white'}
          size="xs"
          text={areAllSelected ? 'Unselect All' : 'Select All'}
          icon={CheckIcon}
          onClick={selectAllClickHandler}
        />
      </Filter>
      <div
        ref={ref}
        className={`odin-flex odin-flex-col odin-gap-3 
        sm:odin-grid sm:odin-grid-cols-2 lg:odin-grid-cols-4`}
      >
        {jobsiteWorkers?.map((jobsiteWorker) => (
          <FormSubmissionSelectableWorker
            key={jobsiteWorker.jobsiteWorkerId}
            jobsiteWorker={jobsiteWorker}
            isSelected={value?.some(
              (sw) =>
                sw.jobsiteWorker.jobsiteWorkerId ===
                  jobsiteWorker.jobsiteWorkerId &&
                sw.changeType !== ChangeType.Removed,
            )}
            onClick={(): void => toggleItem(jobsiteWorker)}
            showContractor={allowMultipleContractors}
          />
        ))}
      </div>
    </div>
  );
});
